import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Filter, Eye, UserPlus, MoreHorizontal, Phone, Mail, MapPin, Trash2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getServiceRequests, updateServiceRequest, deleteServiceRequest, getEmployees } from '../../services/api';
import MaterialUsagePanel from '../../components/inventory/MaterialUsagePanel';
import { useUnread } from '../../context/UnreadContext';
import { UnreadDot } from '../../components/dashboard/UnreadBadge';
import { nextOwnStatus } from '../../lib/progress';
import { NewServiceRequestDialog } from '../../components/dashboard/NewServiceRequestDialog';
import { PRIORITIES, SORT_OPTIONS, applianceLabel, formatCreated, priorityInfo, priorityOf, sortRequests, warrantyLabel } from '../../lib/serviceRequests';
import { ServiceReportForm, hasReport } from '../../components/dashboard/ServiceReportForm';

const appointmentLabel = (request) =>
  request.appointment_date ? `${request.appointment_date} à ${request.appointment_time}` : 'À planifier';

export const ServiceRequestsPage = () => {
  const { user, can } = useAuth();
  const canEditAll = can('service_requests.edit', 'all');
  const canEditOwn = can('service_requests.edit');
  const canAssign = can('service_requests.assign');
  const canDelete = can('service_requests.delete');
  const canCreate = can('service_requests.create');
  const [newOpen, setNewOpen] = useState(false);
  const { isUnread, markRead } = useUnread();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortMode, setSortMode] = useState('priority');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);


  const fetchData = useCallback(async () => {
    try {
      const [requestsRes, employeesRes] = await Promise.all([
        getServiceRequests(),
        canAssign ? getEmployees() : Promise.resolve({ data: [] })
      ]);
      setRequests(requestsRes.data);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [canAssign]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await updateServiceRequest(requestId, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };


  const handleAssignEmployee = async (requestId, employeeId) => {
    try {
      await updateServiceRequest(requestId, { 
        assigned_employee_id: employeeId,
        status: 'assigned'
      });
      toast.success('Employé assigné');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };


  const handleDelete = async (request) => {
    const clientName = `${request.first_name || ''} ${request.last_name || ''}`.trim();
    const confirmed = window.confirm(
      `Supprimer définitivement l'appel de service de ${clientName || 'ce client'}? Cette action est irréversible.`
    );
    if (!confirmed) return;


    try {
      await deleteServiceRequest(request.id);
      setRequests((current) => current.filter((item) => item.id !== request.id));
      if (selectedRequest?.id === request.id) {
        setSelectedRequest(null);
        setDetailsOpen(false);
      }
      toast.success('Appel de service supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };


  const filteredRequests = sortRequests(requests.filter(req => {
    const matchesSearch = 
      req.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const priority = priorityOf(req) || 'none';
    const matchesPriority = priorityFilter === 'all' || priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  }), sortMode);

  const handlePriorityChange = async (request, value) => {
    const priority = value === 'none' ? null : value;
    try {
      await updateServiceRequest(request.id, { priority });
      setRequests(current => current.map(item => (item.id === request.id ? { ...item, priority, urgency: null } : item)));
      setSelectedRequest(current => (current?.id === request.id ? { ...current, priority, urgency: null } : current));
      toast.success('Priorité mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la priorité');
    }
  };


  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      confirmed: 'badge-confirmed',
      assigned: 'badge-confirmed',
      in_progress: 'badge-in-progress',
      completed: 'badge-completed',
      cancelled: 'badge-cancelled'
    };
    return badges[status] || 'badge-pending';
  };


  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      assigned: 'Assigné',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }


  return (
    <div className="space-y-6" data-testid="service-requests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Appels de service</h1>
          <p className="text-slate-500">Gérez les demandes de service clients</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setNewOpen(true)}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="new-service-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel appel
          </Button>
        )}
      </div>

      <NewServiceRequestDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={() => fetchData()}
        employees={employees}
        canAssign={canAssign}
      />


      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmé</SelectItem>
                <SelectItem value="assigned">Assigné</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="completed">Terminé</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="priority-filter">
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorités</SelectItem>
                {PRIORITIES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${item.dot}`}></span>{item.label}</span>
                  </SelectItem>
                ))}
                <SelectItem value="none">Sans priorité</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="w-full sm:w-56" data-testid="sort-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>


      {/* List */}
      <Card>
        <CardContent className="p-0">
          {filteredRequests.length > 0 ? (
            <div className="divide-y">
              {filteredRequests.map((request) => (
                <div 
                  key={request.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`request-row-${request.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="relative w-12 h-12 shrink-0 rounded-full bg-[#1a3a5c] flex items-center justify-center text-white font-semibold">
                          {request.first_name?.[0]}{request.last_name?.[0]}
                          <UnreadDot show={isUnread('service_requests', request.id)} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1a3a5c] flex flex-wrap items-center gap-2">
                            {request.first_name} {request.last_name}
                            {priorityInfo(request) && (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${priorityInfo(request).badge}`}
                                data-testid={`priority-${request.id}`}
                              >
                                <span className={`w-2 h-2 rounded-full ${priorityInfo(request).dot}`}></span>
                                {priorityInfo(request).label}
                              </span>
                            )}
                          </h3>
                          {hasReport(request) && (
                            <p className="text-xs text-green-700 mt-0.5">Rapport d'intervention rempli</p>
                          )}
                          {request.appliance_type && (
                            <p className="text-sm text-slate-500">
                              {[applianceLabel(request.appliance_type), request.brand, request.model].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-4 h-4" />
                            {request.address}
                          </p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {request.phone}
                          </p>
                        </div>
                      </div>
                    </div>


                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="text-sm">
                        <p className="text-xs text-slate-500" data-testid={`created-${request.id}`}>
                          Créé le {formatCreated(request.created_at) || '-'}
                        </p>
                        <p className="font-medium text-[#1a3a5c]">
                          {appointmentLabel(request)}
                        </p>
                        {request.assigned_employee_name && (
                          <p className="text-slate-500">
                            Assigné: {request.assigned_employee_name}
                          </p>
                        )}
                      </div>


                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>


                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setDetailsOpen(true);
                            markRead('service_requests', request.id);
                          }}
                          data-testid={`view-details-${request.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>


                        {!canEditAll && canEditOwn && request.assigned_employee_id === user?.id && nextOwnStatus(request.status) && (
                          <Button
                            size="sm"
                            className="bg-[#00b4d8] hover:bg-[#009bb8]"
                            onClick={() => handleStatusUpdate(request.id, nextOwnStatus(request.status).value)}
                            data-testid={`progress-request-${request.id}`}
                          >
                            {nextOwnStatus(request.status).label}
                          </Button>
                        )}

                        {(canEditAll || canDelete) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEditAll && (<>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(request.id, 'confirmed')}>
                                Confirmer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(request.id, 'in_progress')}>
                                En cours
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(request.id, 'completed')}>
                                Terminé
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleStatusUpdate(request.id, 'cancelled')}
                                className="text-red-600"
                              >
                                Annuler
                              </DropdownMenuItem>
                              </>)}
                              {canDelete && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(request)}
                                className="text-red-700 focus:text-red-700"
                                data-testid={`delete-request-${request.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <p>Aucun appel de service trouvé</p>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails de l'appel de service</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Téléphone</p>
                  <p className="font-medium">{selectedRequest.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Courriel</p>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Créé le</p>
                  <p className="font-medium">
                    {formatCreated(selectedRequest.created_at) || '-'}
                    {selectedRequest.created_by_name ? ` · par ${selectedRequest.created_by_name}` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Rendez-vous</p>
                  <p className="font-medium">{appointmentLabel(selectedRequest)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-500">Adresse</p>
                <p className="font-medium">{selectedRequest.address}</p>
              </div>

              {(selectedRequest.appliance_type || selectedRequest.brand || selectedRequest.model
                || warrantyLabel(selectedRequest.under_warranty)) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Appareil</p>
                    <p className="font-medium">{applianceLabel(selectedRequest.appliance_type) || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Marque / modèle</p>
                    <p className="font-medium">{[selectedRequest.brand, selectedRequest.model].filter(Boolean).join(' ') || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Sous garantie</p>
                    <p className="font-medium">{warrantyLabel(selectedRequest.under_warranty) || 'Ne sait pas'}</p>
                  </div>
                </div>
              )}


              {selectedRequest.problem_description && (
                <div>
                  <p className="text-sm text-slate-500">Problème signalé par le client</p>
                  <p className="font-medium bg-slate-50 p-3 rounded-lg whitespace-pre-line">{selectedRequest.problem_description}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-slate-500 mb-1">Code de priorité</p>
                {canEditAll ? (
                  <div className="flex flex-wrap gap-2" data-testid="priority-picker">
                    {PRIORITIES.map((item) => {
                      const active = priorityOf(selectedRequest) === item.value;
                      return (
                        <Button
                          key={item.value}
                          type="button"
                          size="sm"
                          variant={active ? 'default' : 'outline'}
                          className={active ? `${item.button} border-transparent shadow` : ''}
                          onClick={() => handlePriorityChange(selectedRequest, active ? 'none' : item.value)}
                          data-testid={`set-priority-${item.value}`}
                        >
                          <span className={`w-2.5 h-2.5 rounded-full mr-2 ${item.dot}`}></span>
                          {item.label} · {item.hint}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="font-medium flex items-center gap-2">
                    {priorityInfo(selectedRequest)
                      ? <><span className={`w-2.5 h-2.5 rounded-full ${priorityInfo(selectedRequest).dot}`}></span>{priorityInfo(selectedRequest).label} · {priorityInfo(selectedRequest).hint}</>
                      : 'Aucune'}
                  </p>
                )}
              </div>

              <ServiceReportForm
                request={selectedRequest}
                canEdit={canEditAll || (canEditOwn && selectedRequest.assigned_employee_id === user?.id)}
                onSaved={(updated) => {
                  setSelectedRequest(updated);
                  setRequests(current => current.map(item => (item.id === updated.id ? { ...item, ...updated } : item)));
                }}
              />


              <MaterialUsagePanel
                parentType="service_request"
                parentId={selectedRequest.id}
                user={user}
              />

              {canAssign && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Assigner un employé</p>
                  <Select 
                    value={selectedRequest.assigned_employee_id || ''} 
                    onValueChange={(value) => handleAssignEmployee(selectedRequest.id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un employé" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default ServiceRequestsPage;
